import * as React from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import ReCAPTCHA from "react-google-recaptcha";
import type { z } from "zod";
import { SubmitFeedbackInputSchema } from "../../dto/feedback";
import { FeedbackType, FeedbackDeviceType } from "../../lib/enums";
import { trpc } from "../../client";
import { Button } from "../Button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { PillChoice } from "./PillChoice";

declare const __BUILD_RECAPTCHA_SITE_KEY__: string;
const RECAPTCHA_SITE_KEY = __BUILD_RECAPTCHA_SITE_KEY__ || "";

type FeedbackFormValues = z.infer<typeof SubmitFeedbackInputSchema>;

const TYPE_OPTIONS: { value: FeedbackType; label: string }[] = [
  { value: FeedbackType.Bug, label: "Bug" },
  { value: FeedbackType.FeatureRequest, label: "Feature Request" },
  { value: FeedbackType.Comment, label: "Comment/Question/Suggestion" },
];

const DEVICE_TYPE_OPTIONS: { value: FeedbackDeviceType; label: string }[] = [
  { value: FeedbackDeviceType.Mobile, label: "Mobile device (iPhone, Android, etc.)" },
  { value: FeedbackDeviceType.Tablet, label: "Tablet (iPad, etc.)" },
  { value: FeedbackDeviceType.Desktop, label: "Desktop (Laptop, desktop, etc.)" },
];

interface FeedbackModalProps {
  isAuthenticated?: boolean;
  onClose: () => void;
}

export function FeedbackModal({ isAuthenticated = false, onClose }: FeedbackModalProps) {
  const recaptchaRef = React.useRef<ReCAPTCHA>(null);
  const [submitted, setSubmitted] = React.useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm<FeedbackFormValues>({
    resolver: zodResolver(SubmitFeedbackInputSchema),
    defaultValues: {
      recaptcha_token: RECAPTCHA_SITE_KEY ? "" : "disabled",
    },
  });

  const selectedType = useWatch({ control, name: "type" });
  const selectedDeviceType = useWatch({ control, name: "bug_device_type" });

  const mutation = trpc.website.submitFeedback.useMutation({
    onSuccess: () => setSubmitted(true),
    onError: () => {
      if (RECAPTCHA_SITE_KEY) {
        recaptchaRef.current?.reset();
        setValue("recaptcha_token", "");
      }
    },
  });

  const onSubmit = (data: FeedbackFormValues) => {
    mutation.mutate(data);
  };

  const handleRecaptchaChange = (token: string | null) => {
    setValue("recaptcha_token", token ?? "", { shouldValidate: true });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Submit Feedback</DialogTitle>
        </DialogHeader>

        {submitted ? (
          <div className="text-satyrs-gold py-6 text-center">
            <p className="m-0 mb-2 text-lg font-semibold">Thanks for the feedback!</p>
            <p className="text-muted-foreground m-0">We appreciate you taking the time.</p>
            <Button onClick={onClose} className="mt-4">
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
            <div>
              <PillChoice
                label="What would you like to report?"
                options={TYPE_OPTIONS}
                value={selectedType}
                onChange={(value) => setValue("type", value, { shouldValidate: true })}
              />
              {errors.type && <p className="mt-1 text-sm text-red-500">Please make a selection</p>}
            </div>

            {!isAuthenticated && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="feedback-name">Your Name *</Label>
                  <Input id="feedback-name" type="text" {...register("name")} />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feedback-email">Your Email *</Label>
                  <Input id="feedback-email" type="email" {...register("email")} />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>
                  )}
                </div>
              </>
            )}

            {selectedType === FeedbackType.Bug && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="bug-url">What URL were you on when the issue occurred?</Label>
                  <Input id="bug-url" type="text" {...register("bug_url")} />
                  {errors.bug_url && (
                    <p className="mt-1 text-sm text-red-500">{errors.bug_url.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bug-steps">What were you doing when the issue occurred?</Label>
                  <Textarea id="bug-steps" rows={3} {...register("bug_steps")} />
                  {errors.bug_steps && (
                    <p className="mt-1 text-sm text-red-500">{errors.bug_steps.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bug-expected">What would you expect the behavior to be?</Label>
                  <Textarea id="bug-expected" rows={3} {...register("bug_expected")} />
                  {errors.bug_expected && (
                    <p className="mt-1 text-sm text-red-500">{errors.bug_expected.message}</p>
                  )}
                </div>
                <div>
                  <PillChoice
                    label="What kind of device did the issue appear on?"
                    options={DEVICE_TYPE_OPTIONS}
                    value={selectedDeviceType ?? undefined}
                    onChange={(value) =>
                      setValue("bug_device_type", value, { shouldValidate: true })
                    }
                  />
                  {errors.bug_device_type && (
                    <p className="mt-1 text-sm text-red-500">{errors.bug_device_type.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bug-browser">
                    Which browser were you using (Chrome, Edge, etc)? What version?
                  </Label>
                  <Input id="bug-browser" type="text" {...register("bug_browser")} />
                  {errors.bug_browser && (
                    <p className="mt-1 text-sm text-red-500">{errors.bug_browser.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bug-os">
                    What operating system did the issue occur on? (MacOS, Windows, Linux, etc.)
                  </Label>
                  <Input id="bug-os" type="text" {...register("bug_os")} />
                  {errors.bug_os && (
                    <p className="mt-1 text-sm text-red-500">{errors.bug_os.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bug-other-info">Any other information you can provide</Label>
                  <Textarea id="bug-other-info" rows={3} {...register("bug_other_info")} />
                </div>
              </>
            )}

            {selectedType === FeedbackType.FeatureRequest && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="feature-description">
                    Please describe the feature you would like to see
                  </Label>
                  <Textarea
                    id="feature-description"
                    rows={3}
                    {...register("feature_description")}
                  />
                  {errors.feature_description && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.feature_description.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feature-page">Which page would this feature be on?</Label>
                  <Input id="feature-page" type="text" {...register("feature_page")} />
                  {errors.feature_page && (
                    <p className="mt-1 text-sm text-red-500">{errors.feature_page.message}</p>
                  )}
                </div>
              </>
            )}

            {selectedType === FeedbackType.Comment && (
              <div className="space-y-2">
                <Label htmlFor="comment-message">Please leave your feedback</Label>
                <Textarea id="comment-message" rows={4} {...register("comment_message")} />
                {errors.comment_message && (
                  <p className="mt-1 text-sm text-red-500">{errors.comment_message.message}</p>
                )}
              </div>
            )}

            {RECAPTCHA_SITE_KEY && (
              <div>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  theme="dark"
                  onChange={handleRecaptchaChange}
                  onExpired={() => setValue("recaptcha_token", "")}
                />
                {errors.recaptcha_token && (
                  <p className="mt-1 text-sm text-red-500">{errors.recaptcha_token.message}</p>
                )}
              </div>
            )}

            {mutation.error && (
              <p className="text-sm text-red-500">Something went wrong. Please try again.</p>
            )}

            <Button type="submit" className="w-full" disabled={mutation.isPending}>
              {mutation.isPending ? "Sending..." : "Submit Feedback"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
